import { Outlet } from 'react-router-dom';
import { Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar, Badge } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NotificationsIcon from '@mui/icons-material/Notifications';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import MessageIcon from '@mui/icons-material/Message';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { TopAppBar } from './TopAppBar';
import { usePatientNutritionNotifications } from '../hooks/usePatientNutritionNotifications';
import { usePatientTodoSummary } from '../hooks/usePatientTodoSummary';

const drawerWidth = 240;

interface PatientNavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  isBadgeTarget?: 'notifications' | 'questions';
}

const patientNavItems: PatientNavItem[] = [
  { label: 'Profile', path: '/patient/profile', icon: <PersonIcon /> },
  { label: 'My Protocols', path: '/patient/my-protocols', icon: <AssignmentIcon /> },
  { label: 'Check-in', path: '/patient/home', icon: <HomeIcon /> },
  { label: 'Notifications', path: '/patient/notifications', icon: <NotificationsIcon />, isBadgeTarget: 'notifications' },
  { label: 'Ask Nutritionist', path: '/patient/questions', icon: <QuestionAnswerIcon />, isBadgeTarget: 'questions' },
  { label: 'Care Network', path: '/patient/care-network', icon: <PeopleIcon /> },
  { label: 'NutriChat AI', path: '/patient/messages', icon: <MessageIcon /> },
];

/**
 * Patient Portal Layout
 * Used for all patient-facing screens (P0-P5)
 */
export const PatientPortalLayout = () => {
  const { pendingQuestionCount } = usePatientNutritionNotifications();
  const { dueCount } = usePatientTodoSummary();

  const getBadgeCount = (target?: string): number | undefined => {
    if (target === 'notifications') return dueCount > 0 ? dueCount : undefined;
    if (target === 'questions') return pendingQuestionCount > 0 ? pendingQuestionCount : undefined;
    return undefined;
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <TopAppBar />
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {patientNavItems.map((item) => {
              const badgeCount = getBadgeCount(item.isBadgeTarget);
              return (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton component={RouterLink} to={item.path}>
                    <ListItemIcon>
                      {badgeCount ? (
                        <Badge badgeContent={badgeCount} color="error">
                          {item.icon}
                        </Badge>
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};
